'use client';

import { useTranslations } from 'next-intl';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/shadcn/alert-dialog';
import { Button } from '@/shared/ui/shadcn/button';
import { NoticeBanner } from '@/shared/ui/NoticeBanner';
import type {
  AngleDeleteConflictMeta,
  DeleteConflictStrategy,
} from '@/types/project-angle';

interface AngleDeleteConflictDialogProps {
  /** Conflict payload from the 409 response, or null when closed. */
  conflict: AngleDeleteConflictMeta | null;
  /** Server-localized 409 `detail` — the blocked-dialog heading (verbatim). */
  detail?: string | null;
  isPending: boolean;
  onChoose: (strategy: DeleteConflictStrategy) => void;
  onCancel: () => void;
}

/**
 * Confirmation dialog for the cross-concept view-delete conflict (409).
 *
 * Shared by the Brief view-library delete (`DELETE /angles/{id}`) and the
 * concept-page view delete (`DELETE /concepts/{id}/angles/{conceptAngleId}`),
 * which is why it lives in `shared/ui` rather than a single feature.
 *
 * All copy is server-driven (`detail`, section `message`s, strategy `label`s) —
 * rendered verbatim per the API-driven messages rule. The two sections use the
 * shared NoticeBanner: `has_renders` (warning, the destructive consequence) and
 * `is_main_elsewhere` (info, the protected concepts). Strategy buttons come from
 * `available_strategies`; a destructive style maps to the destructive button.
 *
 * Mirrors the cost-estimate-overwrite / chain-owner-conflict pattern on the
 * other platforms — keep button order Cancel → non-destructive → destructive.
 *
 * Blocked (informational) mode: when `available_strategies` is empty (the view
 * is the main view of other designs → `is_blocked`), the server offers NO
 * destructive option. We render a single-banner informational dialog with one
 * dismiss button telling the user to delete the dependent designs first — the
 * `is_main_elsewhere.message` carries that server-localized guidance verbatim.
 */
export function AngleDeleteConflictDialog({
  conflict,
  detail,
  isPending,
  onChoose,
  onCancel,
}: AngleDeleteConflictDialogProps) {
  const t = useTranslations('angles');
  const tCommon = useTranslations('common');

  const hasRenders = conflict?.conflicts.has_renders;
  const isMainElsewhere = conflict?.conflicts.is_main_elsewhere;

  // Non-destructive strategies first, destructive last (button-order parity).
  const strategies = [...(conflict?.available_strategies ?? [])].sort((a, b) =>
    a.style === 'destructive' ? 1 : b.style === 'destructive' ? -1 : 0
  );

  // Blocked = no strategy to offer. `available_strategies.length` is the robust
  // primary signal (works even if the explicit `is_blocked` flag is absent).
  const isBlocked = strategies.length === 0;

  return (
    <AlertDialog
      open={!!conflict}
      onOpenChange={(open) => !open && onCancel()}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          {/* Blocked: server `detail` ("This view is used by other designs") for
              parity with Apple/Android; otherwise the generic delete title. */}
          <AlertDialogTitle>
            {isBlocked && detail ? detail : t('deleteConfirmTitle')}
          </AlertDialogTitle>
        </AlertDialogHeader>

        <div className="flex flex-col gap-3">
          {/* Blocked: show ONLY the actionable guidance ("delete those designs
              first") — the has_renders count is 0 here, so skip that banner. */}
          {!isBlocked && hasRenders && hasRenders.count > 0 && (
            <NoticeBanner variant="warning" message={hasRenders.message} />
          )}
          {isMainElsewhere && isMainElsewhere.count > 0 && (
            <NoticeBanner variant="info" message={isMainElsewhere.message} />
          )}
        </div>

        <AlertDialogFooter>
          {isBlocked ? (
            // Informational: a single dismiss button, no destructive action.
            <Button variant="secondary" onClick={onCancel} disabled={isPending}>
              {tCommon('close')}
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={onCancel} disabled={isPending}>
                {tCommon('cancel')}
              </Button>
              {strategies.map((strategy) => (
                <Button
                  key={strategy.key}
                  variant={
                    strategy.style === 'destructive'
                      ? 'destructive'
                      : 'secondary'
                  }
                  onClick={() => onChoose(strategy.key)}
                  disabled={isPending}
                >
                  {strategy.label}
                </Button>
              ))}
            </>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
